import React, { useEffect } from 'react';
import { IStore } from "../../../redux/reducers";
import { useDispatch, useSelector } from "react-redux";
import * as JobActions from '../../../redux/actions/jobAction';
import * as UploadAction from '../../../redux/actions/dataset';
import * as GrainTypeAction from 'src/redux/actions/grainTypeAction';
import { View, FlatList, RefreshControl } from 'react-native';
import { IJob, IJobs, ISearchQuery, IOrgJobListItem, IDateRange } from '../../../models';
import OrgJobHistoryListItem from '../../listItem/OrgJobHistoryItem';
import FilterModal from '../../modal/FilterModal';
import { Button, HStack, useToast } from 'native-base';
import { isDisplayableJob } from '../../../utils/jobUtils';
import { listStyle } from 'src/styles/listStyle';
import { containerStyle } from 'src/styles/containerStyle';
import { downloadJobListReport, downloadScannedImageFromId } from 'src/services/download';
import { JobStatus } from '../../../models';
import { generateReadableDatasetName } from 'src/utils/datasetUtils';
import { isElectron } from 'src/utils/miscUtils';
import MaterialIconByName from 'src/components/MaterialIconByName';
import { getText } from 'src/utils/i18n';
import * as GrainAPI from 'src/api/grain';
import {
  isSameGrainJobReportError,
  resolveJobReportDateFilter,
  resolveJobReportCenterIds,
  validateJobReportGrainIds
} from 'src/utils/jobListReportFilters';
export default function OrgJobHistoryList({ navigation }: { navigation: any }) {

  const currentStack = useSelector((store: IStore) => store.app.currentStack);

  // mlAdmin-specific job filtering logic
  const isDisplayableJobForStack = (status: JobStatus) => {
    if (currentStack === 'mlAdmin') {
      return [JobStatus.JOB_COMPLETED].includes(status);
    }
    return isDisplayableJob(status);
  };

  const [refreshing, setRefreshing] = React.useState(false);
  const [downloadingAll, setDownloadingAll] = React.useState(false);
  const toast = useToast();
  
  const orgJobs: IJobs | null | undefined = useSelector((store: IStore) => store.job.orgJobs.jobs);
  
  const centers = useSelector((store: IStore) => store.org.centers.centers);
  const grainTypes = useSelector((store: IStore) => store.user.userProfile.response?.grains);
  const grainIdFilter = useSelector((store: IStore) => store.app.grainIdFilter) || "all";
  const selectedGrainId = useSelector((store: IStore) => store.grainType.grainId) || "all";
  const centerIdFilter = useSelector((store: IStore) => store.app.centerIdFilter) || "all";
  const searchQuery: ISearchQuery | undefined = useSelector((store: IStore) => store.app.searchQuery);
  const stateFilter = useSelector((store: IStore) => store.app.stateFilter) || "all";
  const districtFilter = useSelector((store: IStore) => store.app.districtFilter) || "all";
  const dateRange: IDateRange | undefined = useSelector((store: IStore) => store.app.dateRange);
  const orgId: string | undefined = useSelector((store: IStore) => store.user.userProfile.response?.org?.orgId);
  const locale = useSelector((store: IStore) => store.app.locale);
  const isOnline = useSelector((store: IStore) => store.app.isOnline);

  const dispatch = useDispatch();

  const [listData, setListData] = React.useState<IOrgJobListItem[]>([]);
  const [localProfileNamesByKey, setLocalProfileNamesByKey] = React.useState<Record<string, string>>({});
  const profileLookupGrainIds = React.useMemo(() => {
    const jobs = orgJobs?.Items?.filter((item): item is IJob => item !== null && item !== undefined) || [];
    return Array.from(new Set(jobs.map((job) => job.grainId).filter(Boolean))).sort();
  }, [orgJobs]);
  const profileLookupGrainIdsKey = profileLookupGrainIds.join('|');

  useEffect(() => {
    fetchOrgJobs();
  }, [orgId]);

  useEffect(() => {
    if (!isElectron() || typeof window === 'undefined') {
      return;
    }

    const electron = (window as any).electron;
    if (!electron?.getGrainProfiles) {
      return;
    }

    electron.getGrainProfiles()
      .then((profiles: any[]) => {
        if (!Array.isArray(profiles)) return;
        const next: Record<string, string> = {};
        profiles.forEach((profile) => {
          if (profile?.grainId && profile?.profileId && profile?.profileName) {
            next[`${profile.grainId}:${profile.profileId}`] = profile.profileName;
          }
        });
        setLocalProfileNamesByKey(next);
      })
      .catch((error: Error) => {
        console.warn('Failed to load local profile names for org history:', error);
      });
  }, [orgId, orgJobs]);

  useEffect(() => {
    if (isOnline === false) {
      return;
    }

    const grainIds = profileLookupGrainIdsKey ? profileLookupGrainIdsKey.split('|') : [];
    if (grainIds.length === 0) {
      return;
    }

    let cancelled = false;

    Promise.all(grainIds.map((grainId) =>
      GrainAPI.getGrainProfiles(String(grainId)).catch(() => [])
    ))
      .then((profileGroups) => {
        if (cancelled) return;
        const next: Record<string, string> = {};
        profileGroups.flat().forEach((profile) => {
          if (profile?.grainId && profile?.profileId && profile?.profileName) {
            next[`${profile.grainId}:${profile.profileId}`] = profile.profileName;
          }
        });
        setLocalProfileNamesByKey((current) => ({ ...current, ...next }));
      });

    return () => {
      cancelled = true;
    };
  }, [isOnline, profileLookupGrainIdsKey]);

  useEffect(() => {
    updateOrgList();
  }, [grainIdFilter, searchQuery, dateRange, centerIdFilter, orgJobs, centers, grainTypes, stateFilter, districtFilter, selectedGrainId, currentStack]);

  const fetchOrgJobs = async () => {
    if (orgId !== undefined) {
      dispatch(JobActions.orgJobs.request({
        'orgId': orgId
      }))
    }
  }

  const fetchMoreOrgJobs = async () => {
    fetchOrgJobs()
  }

  const updateOrgList = () => {
    let listItems: IOrgJobListItem[] = []
    orgJobs?.Items
      .filter((item) => isDisplayableJobForStack(item.jobStatus))
      .filter((item: IJob) => {
        let centerId = undefined
        if (item.info !== undefined && item.info !== null && item.info['centerId'] !== undefined) {
          centerId = item.info['centerId']
        }
        return filterByCenterId(centerId)
      })
      .filter((item) => filterByGrainId(item.grainId))
      .filter((item: IJob) => {
        // For mlAdmin stack, only show jobs with the same grainId as currently selected
        if (currentStack === 'mlAdmin' && selectedGrainId && selectedGrainId !== 'all') {
          return item.grainId === selectedGrainId;
        }
        return true;
      })
      .filter((item: IJob) => filterBySearchQuery(item))
      .filter((item: IJob) => filterByDateRange(item))
      .filter((item: IJob) => filterByState(item))
      .filter((item: IJob) => filterByDistrict(item))
      .forEach((item) => {
        let centerName = ""
        if (item.info !== undefined && item.info !== null && item.info['centerId'] !== undefined) {
          centerName = getCenterName(item.info['centerId'])
        }

        listItems.push({
          "jobId": item.jobId,
          "jobStatus": item.jobStatus,
          "grainId": item.grainId,
          "profileId": item.profileId,
          "username": item.username,
          "centerName": centerName,
          "createdAt": item.createdAt
        })
      })
    setListData(listItems)
  }

  const getCenterName = (centerId: string | undefined) => {
    for (var i = 0; i < centers?.length; i++) {
      if (centerId === centers[i].centerId) {
        return centers[i].name
      }
    }
    return ""
  }

  const filterByGrainId = (id: any) => {
    if (grainIdFilter === undefined
      || grainIdFilter === ""
      || grainIdFilter === "0"
      || grainIdFilter === "all") {
      return true
    }
    return id === grainIdFilter
  }

  const filterBySearchQuery = (item: IJob) => {
    if (searchQuery === undefined || searchQuery?.query === undefined || searchQuery?.query === "") {
      return true
    }

    if (searchQuery.type === 'jobId') {
      return item.jobId.includes(searchQuery.query)
    }
    return false
  }

  const filterByDateRange = (item: IJob) => {
    if (dateRange === undefined || dateRange?.from === undefined || dateRange?.to === undefined) {
      return true
    }

    return item.createdAt >= dateRange.from && item.createdAt <= dateRange.to;
  }

  const filterByCenterId = (id: any) => {
    if (centerIdFilter === ""
      || centerIdFilter === undefined
      || centerIdFilter === "all") {
      return true
    }
    return centerIdFilter === id
  }
  const filterByState = (item: IJob) => {
    if (stateFilter === "all") {
      return true
    }

    const centerId = item.info?.centerId;
    if (centerId) {
      const center = centers.find(c => c.centerId === centerId);
      return center ? center.state === stateFilter : false;
    }

    return false;
  }

  const filterByDistrict = (item: IJob) => {
    if (districtFilter === "all") {
      return true
    }

    const centerId = item.info?.centerId;
    if (centerId) {
      const center = centers.find(c => c.centerId === centerId);
      return center ? center.addressDistrict === districtFilter : false;
    }

    return false;
  }

  const getFilteredCenterIds = (): string[] | undefined => {
    return resolveJobReportCenterIds(
      centers || [],
      centerIdFilter,
      stateFilter,
      districtFilter
    );
  };

  const downloadFilteredJobs = async () => {
    if (!orgId) {
      return;
    }

    if (!validateJobReportGrainIds(listData).isValid) {
      toast.show({
        title: getText('downloadFailed'),
        description: getText('jobListReportSameGrainRequired'),
        duration: 5000,
        bg: 'error.600'
      });
      return;
    }

    setDownloadingAll(true);
    try {
      const effectiveGrainId = currentStack === 'mlAdmin' && selectedGrainId !== 'all'
        ? selectedGrainId
        : grainIdFilter;
      const filteredCenterIds = getFilteredCenterIds();
      const downloaded = await downloadJobListReport({
        scope: { type: 'organization', orgId },
        filters: {
          ...(effectiveGrainId !== 'all' ? { grainId: effectiveGrainId } : {}),
          ...(searchQuery?.type === 'jobId' && searchQuery.query ? { jobId: searchQuery.query } : {}),
          ...resolveJobReportDateFilter(dateRange, 'createdAt'),
          ...(filteredCenterIds !== undefined ? { centerIds: filteredCenterIds } : {}),
          statuses: currentStack === 'mlAdmin'
            ? [JobStatus.JOB_COMPLETED]
            : [
              JobStatus.JOB_IMAGE_UPLOADED,
              JobStatus.JOB_PROCESSING_LOCAL,
              JobStatus.JOB_IN_PROGRESS,
              JobStatus.JOB_SYNCING,
              JobStatus.JOB_COMPLETED,
              JobStatus.JOB_FAILED
            ]
        },
        deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata',
        locale,
        output_format: 'xls'
      });
      if (!downloaded) {
        return;
      }
      toast.show({
        title: getText('downloadSuccessful'),
        description: getText('jobListReportDownloadSuccess'),
        duration: 3000
      });
    } catch (error) {
      toast.show({
        title: getText('downloadFailed'),
        description: getText(
          isSameGrainJobReportError(error)
            ? 'jobListReportSameGrainRequired'
            : 'jobListReportDownloadFailed'
        ),
        duration: 5000
      });
    } finally {
      setDownloadingAll(false);
    }
  };

  const renderItem = ({ item }: { item: IOrgJobListItem }) => {
    return <OrgJobHistoryListItem
      item={item}
      downloadImageCallback={async () => {
        await downloadScannedImageFromId(item.jobId)
      }}
      itemCallback={() => {
        const selectedJobStatus = orgJobs?.Items.find((job) => job.jobId === item.jobId);
        if (selectedJobStatus !== undefined) {
          dispatch(JobActions.jobStatus.success(selectedJobStatus));
          dispatch(JobActions.pollJobStatus.request(item.jobId));
          
          if (currentStack === 'mlAdmin') {
            dispatch(UploadAction.clearDataset());
            
            const readableDatasetId = generateReadableDatasetName(
              selectedJobStatus.grainId, 
              grainTypes, 
              undefined,
              item.jobId
            );
            
            dispatch(JobActions.setJobId(readableDatasetId));
            dispatch(UploadAction.setId(readableDatasetId));
            if (selectedJobStatus.grainId) {
              dispatch(GrainTypeAction.setGrainId(selectedJobStatus.grainId));
            }
            dispatch(UploadAction.setIsJob(true));
            dispatch(UploadAction.setIsExistingDataset(false));
            navigation.navigate('CombinedLabelingPage');
          } else {
            dispatch(JobActions.setJobId(item.jobId));
            navigation.navigate('Results', { jobId: item.jobId });
          }
        }
      }}
      retryJobCallback={function (item: IOrgJobListItem): void {
        dispatch(JobActions.queueJob.request({
          'jobId': item.jobId
        }));
      }}
      localProfileName={localProfileNamesByKey[`${item.grainId}:${item.profileId}`]} />
  };

  const renderFooter = () => {
    return (
      <View style={listStyle.footer}>
        <Button onPress={() => {
          fetchMoreOrgJobs()
        }} isLoading={refreshing} variant="subtle">Load more</Button>
      </View>
    );
  };

  return (
    <View style={containerStyle.listContainer}>
      {
        currentStack !== 'orgSuperAdmin'
        && <FilterModal />
      }
      <HStack justifyContent="flex-end" px={2} py={2}>
        <Button
          startIcon={<MaterialIconByName name="file-excel" />}
          onPress={downloadFilteredJobs}
          isLoading={downloadingAll}
          isLoadingText={getText('generatingReport')}
          isDisabled={!orgId}
        >
          {getText('downloadAll')}
        </Button>
      </HStack>
      <FlatList
        data={listData}
        renderItem={renderItem}
        keyExtractor={item => item.jobId}
        ListFooterComponent={renderFooter}
        onEndReached={fetchMoreOrgJobs}
        onEndReachedThreshold={0.3}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={() => {
            fetchOrgJobs();
          }} />
        }
      />
    </View>
  );
};
